import React, { useCallback, useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  TextField,
  Box,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText
} from '@mui/material';
import { 
  useCreatePushTemplateMutation, 
  useUpdatePushTemplateMutation 
} from '../../redux/api/pushTemplatesApi';
import { PushTemplate, PushTemplateType } from '../../resources/types/pushTemplatesTypes';
import { useTranslation } from 'react-i18next';

interface PushTemplateDialogProps extends DialogProps {
  onClose: () => void;
  onSuccess: (message?: string) => void;
  editTemplate?: PushTemplate | null;
}

const templateTypes: { value: PushTemplateType; labelKey: string }[] = [
  { value: 'system', labelKey: 'pushes.typeSystem' },
  { value: 'marketing', labelKey: 'pushes.typeMarketing' },
  { value: 'reward', labelKey: 'pushes.typeReward' },
  { value: 'custom', labelKey: 'pushes.typeCustom' }
];

const PushTemplateDialog: React.FC<PushTemplateDialogProps> = ({ 
  open, 
  onClose,
  onSuccess,
  editTemplate 
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [type, setType] = useState<PushTemplateType>('custom');
  const [cityId, setCityId] = useState('');
  const [variables, setVariables] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [createTemplate, { isLoading: isCreating }] = useCreatePushTemplateMutation();
  const [updateTemplate, { isLoading: isUpdating }] = useUpdatePushTemplateMutation();
  
  const isLoading = isCreating || isUpdating;
  const isEditMode = !!editTemplate;

  useEffect(() => {
    if (open) {
      if (editTemplate) {
        setTitle(editTemplate.title || '');
        setBody(editTemplate.body || '');
        setType(editTemplate.type || 'custom');
        setCityId(editTemplate.cityId || '');
        setVariables(editTemplate.variables || '');
      } else {
        setTitle('');
        setBody('');
        setType('custom');
        setCityId('');
        setVariables('');
      }
      setError(null);
    }
  }, [open, editTemplate]);

  const handleSave = useCallback(async () => {
    setError(null);
    
    if (!title.trim()) {
      setError(t('pushes.errorTitleRequired'));
      return;
    }
    
    if (!body.trim()) {
      setError(t('pushes.errorBodyRequired'));
      return;
    }

    try {
      const payload: any = {
        title: title.trim(),
        body: body.trim(),
        type
      };

      if (cityId) payload.cityId = cityId;
      if (variables) payload.variables = variables;

      if (isEditMode && editTemplate) {
        await updateTemplate({ id: editTemplate._id, body: payload }).unwrap();
        onSuccess(t('pushes.templateUpdated'));
      } else {
        await createTemplate(payload).unwrap();
        onSuccess(t('pushes.templateCreated'));
      }
      
      onClose();
    } catch (err: any) {
      console.error('=== Error saving template:', err);
      const errorMessage = err?.data?.message || err?.message || 
        (isEditMode ? t('pushes.errorUpdateTemplate') : t('pushes.errorCreateTemplate'));
      setError(errorMessage);
    }
  }, [title, body, type, cityId, variables, isEditMode, editTemplate, createTemplate, updateTemplate, onSuccess, onClose, t]);

  const isFormValid = title.trim().length > 0 && body.trim().length > 0;

  return (
    <Dialog fullWidth maxWidth='sm' open={open} onClose={onClose}>
      <DialogTitle>{isEditMode ? t('pushes.editTemplateTitle') : t('pushes.createTemplateTitle')}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <TextField
          fullWidth
          autoFocus={!isEditMode}
          label={t('pushes.templateTitleLabel')}
          margin='normal'
          variant='outlined'
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={isLoading}
          required
        />
        
        <TextField
          fullWidth
          label={t('pushes.bodyLabel')}
          margin='normal'
          variant='outlined'
          multiline
          rows={6}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          disabled={isLoading}
          required
        />

        <FormControl fullWidth margin='normal' required>
          <InputLabel>{t('pushes.templateTypeLabel')}</InputLabel>
          <Select
            value={type}
            onChange={(e) => setType(e.target.value as PushTemplateType)}
            disabled={isLoading}
            label={t('pushes.templateTypeLabel')}
          >
            {templateTypes.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {t(option.labelKey)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label={t('pushes.cityIdLabel')}
          margin='normal'
          variant='outlined'
          value={cityId}
          onChange={(e) => setCityId(e.target.value)}
          disabled={isLoading}
        />

        <TextField
          fullWidth
          label={t('pushes.variablesLabel')}
          margin='normal'
          variant='outlined'
          multiline
          rows={3}
          value={variables}
          onChange={(e) => setVariables(e.target.value)}
          disabled={isLoading}
          helperText={t('pushes.variablesHelper')}
          placeholder={t('pushes.variablesPlaceholder')}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          {t('pushes.cancel')}
        </Button>
        <Button 
          color='primary' 
          variant='contained'
          onClick={handleSave}
          disabled={!isFormValid || isLoading}
        >
          {isLoading ? t('pushes.saving') : (isEditMode ? t('pushes.update') : t('pushes.save'))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PushTemplateDialog;
